import { rotateDirection } from "../direction";
import { isPointsToDirection } from "../interfaces";
import type { PhysicalThing } from "../physicalThing";
import { deserializeThing, serializeThing } from "../serialization";
import type { Thing } from "../thing";
import { MultiTileThingPart } from "../things/multitile/multiTileThingPart";
import { XY } from "../xy";

function xyKey(xy: XY): string {
	return `${xy.x},${xy.y}`;
}

export class SimpleBoard {
	private cellMap: Map<string, PhysicalThing[]> = new Map();
	private nonPhysicalThings: Thing[] = [];

	putThing(thing: PhysicalThing): void {
		const key = xyKey(thing.xy);
		let list = this.cellMap.get(key);
		if (!list) {
			list = [];
			this.cellMap.set(key, list);
		}
		list.push(thing);
	}

	putThingAt(thing: PhysicalThing, xy: XY): void {
		(thing as any).xy = xy;
		this.putThing(thing);
	}

	addNonPhysicalThing(thing: Thing): void {
		this.nonPhysicalThings.push(thing);
	}

	getThingList(): PhysicalThing[] {
		const list: PhysicalThing[] = [];
		for (const things of this.cellMap.values()) {
			list.push(...things);
		}
		return list;
	}

	getNonPhysicalThings(): Thing[] {
		return this.nonPhysicalThings;
	}

	getThingsAt(xy: XY): PhysicalThing[] {
		return this.cellMap.get(xyKey(xy)) ?? [];
	}

	getAllOccupiedXYs(): XY[] {
		const result: XY[] = [];
		for (const [key, things] of this.cellMap) {
			if (things.length > 0) {
				const [x, y] = key.split(",").map(Number);
				result.push(new XY(x, y));
			}
		}
		return result;
	}

	getNumberOfTilesUsed(): number {
		let count = 0;
		for (const things of this.cellMap.values()) {
			if (things.length > 0) count++;
		}
		return count;
	}

	getWidth(): number {
		const things = this.getThingList();
		if (things.length === 0) return 0;
		return Math.max(...things.map((t) => t.xy.x)) + 1;
	}

	getHeight(): number {
		const things = this.getThingList();
		if (things.length === 0) return 0;
		return Math.max(...things.map((t) => t.xy.y)) + 1;
	}

	rotate(turns90: number): void {
		const turns = ((turns90 % 4) + 4) % 4;
		for (let i = 0; i < turns; i++) {
			// transpose then flipV = 90° counter-clockwise
			this.transform((xy) => new XY(xy.y, xy.x));
			this.flipV();
		}
		if (turns === 0) return;
		const rotateEntityDirection = (thing: Thing) => {
			if (!isPointsToDirection(thing)) return;
			const dir = thing.getDirection();
			if (dir === null) return;
			thing.setDirection(rotateDirection(dir, turns));
		};
		for (const thing of this.getThingList()) rotateEntityDirection(thing);
		for (const thing of this.nonPhysicalThings) rotateEntityDirection(thing);
	}

	flipH(): void {
		const things = this.getThingList();
		if (things.length === 0) return;
		const maxX = Math.max(...things.map((t) => t.xy.x));
		this.transform((xy) => new XY(maxX - xy.x, xy.y));
	}

	flipV(): void {
		const things = this.getThingList();
		if (things.length === 0) return;
		const maxY = Math.max(...things.map((t) => t.xy.y));
		this.transform((xy) => new XY(xy.x, maxY - xy.y));
	}

	clone(): SimpleBoard {
		const copy = new SimpleBoard();
		for (const thing of this.getThingList()) {
			const cloned = deserializeThing(serializeThing(thing)) as PhysicalThing;
			copy.putThing(cloned);
		}
		for (const nonPhysical of this.nonPhysicalThings) {
			const cloned = deserializeThing(serializeThing(nonPhysical)) as Thing;
			copy.addNonPhysicalThing(cloned);
		}
		// Multi-tile parts serialize a coreXY pointer that postDeserialize normally
		// resolves on a real Board. Clone runs outside that path, so re-link here.
		const allThings: Thing[] = [...copy.getThingList(), ...copy.nonPhysicalThings];
		for (const thing of copy.getThingList()) {
			if (thing instanceof MultiTileThingPart) {
				thing.relinkUsingThings(allThings);
			}
		}
		return copy;
	}

	trim(): void {
		const things = this.getThingList();
		if (things.length === 0) return;
		const minX = Math.min(...things.map((t) => t.xy.x));
		const minY = Math.min(...things.map((t) => t.xy.y));
		this.transform((xy) => new XY(xy.x - minX, xy.y - minY));
	}

	transform(fn: (xy: XY) => XY): void {
		const newCellMap = new Map<string, PhysicalThing[]>();
		for (const [oldKey, things] of this.cellMap) {
			const [x, y] = oldKey.split(",").map(Number);
			const oldXY = new XY(x, y);
			const newXY = fn(oldXY);
			const newKey = xyKey(newXY);
			for (const thing of things) {
				(thing as any).xy = newXY;
			}
			const existing = newCellMap.get(newKey);
			if (existing) {
				existing.push(...things);
			} else {
				newCellMap.set(newKey, [...things]);
			}
		}
		this.cellMap = newCellMap;
	}
}
